import {
  ButtonHTMLAttributes,
  forwardRef,
  AnchorHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { playSound, SoundType } from '~/lib/audio';
import { useSentry } from '../../contexts/SentryContext';

function getTextContent(
  children: React.ReactNode
): string | null {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    const texts = children
      .map(getTextContent)
      .filter(Boolean);
    return texts.length > 0 ? texts.join(' ') : null;
  }
  return null;
}

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'unstyled';
type ButtonSize = 'default' | 'small';

interface BaseButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: React.ReactNode;
  title?: string;
  sound?: SoundType;
  'data-testid'?: string;
}

interface ButtonAsButton extends BaseButtonProps {
  href?: never;
  to?: never;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disabled?: boolean;
  form?: string;
}

interface ButtonAsLink extends BaseButtonProps {
  /** External URL (e.g., https://example.com) or anchor link (e.g., #section) */
  href?: string;
  /** Internal React Router navigation path (e.g., /login, /dashboard) */
  to?: string;
  onClick?: AnchorHTMLAttributes<HTMLAnchorElement>['onClick'];
  type?: never;
  disabled?: never;
  form?: never;
}

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  unstyled: '',
};

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>((props, ref) => {
  const {
    className = '',
    variant = 'primary',
    size = 'default',
    children,
    sound,
    'data-testid': testId,
    title,
    ...restProps
  } = props;

  const { addBreadcrumb } = useSentry();

  const variantClass = variantClasses[variant];
  const sizeClass = size === 'small' ? 'btn-small' : '';
  const buttonClassName =
    `${variantClass} ${sizeClass} ${className}`.trim();

  // Check if it's a link (has href or to prop)
  if ('href' in restProps || 'to' in restProps) {
    const linkProps = restProps as ButtonAsLink;
    const { href, to, onClick, ...anchorProps } = linkProps;

    const handleLinkClick = (
      e: React.MouseEvent<HTMLAnchorElement>
    ) => {
      const label =
        testId ||
        title ||
        getTextContent(children) ||
        'unknown-button';
      addBreadcrumb(`Click: ${label}`, {
        element: 'button',
        testId,
        title,
      });
      if (sound) {
        playSound(sound);
      }
      onClick?.(e);
    };

    if (to) {
      return (
        <Link
          ref={ref as any}
          to={to}
          className={buttonClassName}
          onClick={handleLinkClick}
          data-testid={testId}
          title={title}
          {...anchorProps}
        >
          {children}
        </Link>
      );
    }

    return (
      <a
        ref={ref as any}
        href={href}
        className={buttonClassName}
        onClick={handleLinkClick}
        data-testid={testId}
        title={title}
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  // Render as button
  const buttonProps = restProps as ButtonAsButton;
  const {
    type = 'button',
    onClick,
    disabled,
    form,
    ...otherButtonProps
  } = buttonProps;

  const resolvedSound = sound ?? soundForType(type);
  const handleButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    const label =
      testId ||
      title ||
      getTextContent(children) ||
      'unknown-button';
    addBreadcrumb(`Click: ${label}`, {
      element: 'button',
      testId,
      title,
    });
    playSound(resolvedSound);
    onClick?.(e);
  };

  return (
    // eslint-disable-next-line custom-rules/prefer-button
    <button
      ref={ref as any}
      type={type}
      className={buttonClassName}
      onClick={handleButtonClick}
      disabled={disabled}
      form={form}
      data-testid={testId}
      title={title}
      {...otherButtonProps}
    >
      {children}
    </button>
  );
});

const soundForType = (type: ButtonProps['type']) => {
  switch (type) {
    case 'submit':
      return SoundType.Confirm;
    case 'reset':
      return SoundType.Cancel;
    case 'button':
    default:
      return SoundType.MenuClick;
  }
};

Button.displayName = 'Button';
